import SignatureCanvas from 'react-signature-canvas';
import moment from 'moment';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import SimpleAlert from '../components/SimpleAlert';

export default function Signing({ }) {
    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const sigCanvas = useRef({});
    const [certifyId, setCertifyId] = useState('');
    const [alert, setAlert] = useState({
        open: false, message: '', close: () => {
            window.location.href = `/`;
        }
    });
    const [checked, setChecked] = useState('');
    const [signName, setSignName] = useState('');
    const [signNameIndex, setSignNameIndex] = useState(0);
    const [isSignFile, setIsSignFile] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [officialSealFile, setOfficialSealFile] = useState(null);
    const [officialSealPreview, setOfficialSealPreview] = useState(null);
    const [sealModalOpen, setSealModalOpen] = useState(false);
    const [signResults, setSignResults] = useState([]);
    const [isClick, setIsClick] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // 使用ref来避免闭包问题
    const signResultsRef = useRef(signResults);
    const signNameIndexRef = useRef(signNameIndex);
    const signNameRef = useRef(signName);
    const isProcessingRef = useRef(isProcessing);

    useEffect(() => { signResultsRef.current = signResults; }, [signResults]);
    useEffect(() => { signNameIndexRef.current = signNameIndex; }, [signNameIndex]);
    useEffect(() => { signNameRef.current = signName; }, [signName]);
    useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);

    const isClickStyle = {
        border: '2px solid red',
    };

    // 从URL获取certifyId
    useEffect(() => {
        document.getElementById('my-zzj-footer').style.display = 'none';
        const cid = searchParams.get("certifyId");
        console.log('Signing页面URL参数解析:', { rawSearch: window.location.search, certifyId: cid });
        if (cid && cid.trim().length > 0) {
            setCertifyId(cid.trim());
        } else {
            console.error('Signing页面缺少有效的certifyId参数');
            setAlert({
                open: true, message: '缺少认证参数，请重新登录', close: () => {
                    window.location.href = `/`;
                }
            });
        }
    }, []);

    // 获取签名信息
    useEffect(() => {
        if (!certifyId) return;

        let isMounted = true;
        const token = localStorage.getItem('adminToken');

        setIsLoading(true);
        console.log('正在获取签名信息:', { certifyId, hasToken: !!token });
        const headers = {};
        if (token) {
            headers['Authorization'] = 'Bearer ' + token;
        }
        fetch(`/api/client/signInfo/${certifyId}`, {
            headers
        })
            .then(res => {
                if (res.status === 401 || res.status === 403) {
                    if (isMounted) {
                        setAlert({
                            open: true, message: '您没有权限执行此操作', close: () => {
                                window.location.href = `/`;
                            }
                        });
                    }
                    return null;
                }
                return res.json();
            })
            .then(res => {
                if (!isMounted) return;
                setIsLoading(false);

                if (!res) {
                    console.error('签名信息API返回空响应');
                    setAlert({
                        open: true, message: '网络错误，请刷新重试', close: () => {
                            setAlert(prev => ({ ...prev, open: false }));
                        }
                    });
                    return;
                }

                if (!res.status) {
                    console.error('签名信息API返回错误:', res);
                    setAlert({
                        open: true, message: res.message || '未知错误', close: () => {
                            setAlert(prev => ({ ...prev, open: false }));
                        }
                    });
                    return;
                }

                const { isNotSignFile, is_legal_person, name, company_name } = res.data || {};
                console.log('签名信息API原始返回:', { isNotSignFile, is_legal_person, name, company_name, rawData: res.data });

                if (isNotSignFile === true) {
                    setAlert({
                        open: true, message: res.message || '当前不需要签名', close: () => {
                            window.location.href = `/`;
                        }
                    });
                    return;
                }

                if (!name || typeof name !== 'string' || name.trim().length === 0) {
                    console.error('签名信息API返回的姓名为空或无效:', { name, data: res.data });
                    setAlert({
                        open: true, message: '签署人姓名不存在，请联系管理人员', close: () => {
                            window.location.href = `/`;
                        }
                    });
                    return;
                }

                const trimmedName = name.trim();
                setIsSignFile(!!is_legal_person);
                setCompanyName(company_name || '');
                setSignName(trimmedName);
                setSignNameIndex(0);
                const signResultList = new Array(trimmedName.length).fill(null);
                setSignResults(signResultList);
                console.log('签名信息加载成功:', { name: trimmedName, length: trimmedName.length, companyName: company_name });
            })
            .catch(err => {
                if (!isMounted) return;
                setIsLoading(false);
                console.error('获取签名信息失败:', err);
                setAlert({
                    open: true, message: '获取签名信息失败，请刷新重试', close: () => {
                        setAlert(prev => ({ ...prev, open: false }));
                    }
                });
            });

        return () => { isMounted = false; };
    }, [certifyId]);

    const getCanvasImage = useCallback(() => {
        if (!sigCanvas.current) return;
        if (sigCanvas.current.isEmpty()) {
            const currentIndex = signNameIndexRef.current;
            const currentName = signNameRef.current;
            setAlert({
                open: true,
                message: currentIndex === currentName.length - 1 ? '点击清空按钮可重新签名' : '请填写指定文字后再确定',
                close: () => {
                    setAlert(prev => ({ ...prev, open: false }));
                }
            });
            return;
        }

        // 防止重复点击处理：先同步更新ref，再触发状态变更，避免竞态窗口
        if (isProcessingRef.current) {
            console.warn('确定按钮被点击，但已有处理在进行中，已忽略');
            return;
        }
        isProcessingRef.current = true; // 同步锁定
        setIsProcessing(true); // 触发UI更新

        const base64Img = sigCanvas.current.toDataURL();
        const img = new Image();
        img.onload = function () {
            createResizedImage(signNameIndexRef.current, img);
        };
        img.onerror = function () {
            setIsProcessing(false);
            isProcessingRef.current = false; // 同步解锁
            setAlert({
                open: true, message: '无效的图片数据', close: () => {
                    setAlert(prev => ({ ...prev, open: false }));
                }
            });
            const currentIndex = signNameIndexRef.current;
            const currentResults = signResultsRef.current;
            const newSignResultList = currentResults.map((item, i) =>
                currentIndex === i ? null : item
            );
            setSignResults(newSignResultList);
        };
        img.src = base64Img;
    }, []);

    const createResizedImage = useCallback((index, img) => {
        const canvas = document.createElement('canvas');
        canvas.width = 50;
        canvas.height = 50;
        const ctx = canvas.getContext('2d');

        // 使用高质量缩小
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, 50, 50);

        // 存储缩小后的图片
        const resizedImg = new Image();
        resizedImg.onload = () => {
            // 图片加载完成后再更新状态，确保拼接时图片已就绪
            const currentResults = signResultsRef.current;
            const newSignResultList = currentResults.map((item, i) =>
                index === i ? resizedImg : item
            );
            setSignResults(newSignResultList);
        };
        resizedImg.onerror = () => {
            setIsProcessing(false);
            isProcessingRef.current = false; // 同步解锁
            setAlert({
                open: true, message: '图片处理失败，请重试', close: () => {
                    setAlert(prev => ({ ...prev, open: false }));
                }
            });
        };
        resizedImg.src = canvas.toDataURL('image/png');
    }, []);

    useEffect(() => {
        const resultPreview = document.getElementById('signResultPreview');
        if (!resultPreview) return;

        const validCount = signResults.filter(img => img !== null).length;
        if (validCount === 0) {
            return;
        }

        console.log('开始签名拼接', { validCount, totalLength: signResults.length });

        let canvasWidth = 50 * signResults.length;
        let canvasHeight = 50;

        // 创建画布
        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d');

        // 保持透明背景，不填充白色背景

        // 绘制图片
        let x = 0;
        signResults.forEach((img, idx) => {
            if (!img) return;
            try {
                ctx.drawImage(img, x, 0);
                x += 50;
            } catch (e) {
                console.error(`绘制第${idx}个字失败:`, e);
            }
        });

        // 应用锐化效果
        applySharpening(ctx, canvasWidth, canvasHeight);

        // 显示结果
        const resultImg = new Image();
        resultImg.onload = () => {
            resultImg.id = 'signBase64';
            resultPreview.innerHTML = '';
            resultPreview.appendChild(resultImg);

            if (sigCanvas.current) sigCanvas.current.clear();

            const currentIndex = signNameIndexRef.current;
            const totalLength = signResults.length;
            const newIndex = currentIndex >= totalLength - 1 ? totalLength - 1 : currentIndex + 1;
            console.log('签名拼接完成，切换到第', newIndex, '个字');
            setSignNameIndex(newIndex);
            setIsProcessing(false);
            isProcessingRef.current = false; // 同步解锁
        };
        resultImg.onerror = (err) => {
            console.error('签名结果图片加载失败:', err);
            setIsProcessing(false);
            isProcessingRef.current = false; // 同步解锁
            setAlert({
                open: true, message: '签名图片生成失败，请重试', close: () => {
                    setAlert(prev => ({ ...prev, open: false }));
                }
            });
        };
        resultImg.src = canvas.toDataURL('image/png');

    }, [signResults]);

    // 应用锐化效果
    const applySharpening = (ctx, width, height) => {
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;

        // 简单的锐化卷积核
        const kernel = [
            [0, -1.0, 0],
            [-1.0, 1 + 4 * 1.0, -1.0],
            [0, -1.0, 0]
        ];

        const tempData = new Uint8ClampedArray(data);

        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                for (let c = 0; c < 3; c++) {
                    let sum = 0;
                    for (let ky = -1; ky <= 1; ky++) {
                        for (let kx = -1; kx <= 1; kx++) {
                            const pixelIndex = ((y + ky) * width + (x + kx)) * 4 + c;
                            sum += tempData[pixelIndex] * kernel[ky + 1][kx + 1];
                        }
                    }
                    const idx = (y * width + x) * 4 + c;
                    data[idx] = Math.max(0, Math.min(255, sum));
                }
            }
        }

        ctx.putImageData(imageData, 0, 0);
    }

    const submit = async () => {
        const currentName = signNameRef.current;
        const currentResults = signResultsRef.current;
        const validCount = currentResults.filter(img => img !== null).length;
        
        console.log('submit 被调用', { validCount, totalLength: currentName.length, currentResults: currentResults.map((item, i) => ({ index: i, isNull: item === null })) });

        if (validCount < currentName.length) {
            console.log('提交被拦截：签名不完整', { validCount, required: currentName.length });
            setAlert({
                open: true, message: `请签完整的姓名后再提交\n（已完成 ${validCount}/${currentName.length}）`, close: () => {
                    setAlert(prev => ({ ...prev, open: false }));
                }
            });
            return;
        }
        console.log('提交通过校验，开始提交');
        if (isSignFile && !officialSealFile) {
            setAlert({
                open: true, message: '请上传公章后再提交', close: () => {
                    setAlert(prev => ({ ...prev, open: false }));
                }
            });
            return;
        }
        if (!checked) {
            setIsClick(true);
            setAlert({
                open: true, message: '请勾选屏幕下方的声明后再提交', close: () => {
                    setAlert(prev => ({ ...prev, open: false }));
                }
            });
            return;
        }

        // 防止重复提交
        if (isProcessingRef.current) {
            console.warn('提交按钮被点击，但已有提交在进行中，已忽略');
            return;
        }
        isProcessingRef.current = true; // 同步锁定
        setIsProcessing(true);

        const token = localStorage.getItem('adminToken');
        const authHeaders = { 'Content-Type': 'application/json' };
        if (token) {
            authHeaders['Authorization'] = 'Bearer ' + token;
        }

        try {
            let res = await fetch(`/api/client/checkSubmitSign`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ certifyId })
            });
            let data = await res.json();
            if (!data || !data.status) {
                setIsProcessing(false);
                isProcessingRef.current = false; // 同步解锁
                setAlert({
                    open: true, message: data?.message || '校验失败，请重试', close: () => {
                        setAlert(prev => ({ ...prev, open: false }));
                    }
                });
                return;
            }

            const signBase64El = document.getElementById('signBase64');
            if (!signBase64El || !signBase64El.src) {
                setIsProcessing(false);
                isProcessingRef.current = false; // 同步解锁
                setAlert({
                    open: true, message: '签名数据不存在，请重新签名', close: () => {
                        setAlert(prev => ({ ...prev, open: false }));
                    }
                });
                return;
            }

            const signBase64 = signBase64El.src;
            const signNameStr = `${moment().format('YYYYMMDDHHmmss')}_${currentName}.png`;
            const signNameFile = base64ToFile(signBase64, signNameStr);
            const signUrl = await upload(signNameFile, token);
            if (!signUrl) {
                setIsProcessing(false);
                isProcessingRef.current = false; // 同步解锁
                setAlert({
                    open: true, message: '签名文件上传失败', close: () => {
                        setAlert(prev => ({ ...prev, open: false }));
                    }
                });
                return;
            }

            let officialSealUrl = null;
            let officialSealName = null;
            if (officialSealFile) {
                const ext = officialSealFile.name.split('.').pop();
                const renamedSealFile = new File([officialSealFile], `${companyName || '法人单位'}公章.${ext}`, { type: officialSealFile.type });
                officialSealUrl = await upload(renamedSealFile, token);
                if (!officialSealUrl) {
                    setIsProcessing(false);
                    isProcessingRef.current = false; // 同步解锁
                    setAlert({
                        open: true, message: '公章上传失败', close: () => {
                            setAlert(prev => ({ ...prev, open: false }));
                        }
                    });
                    return;
                }
                officialSealName = renamedSealFile.name;
            }

            res = await fetch(`/api/client/submitSignInfo`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    certifyId,
                    signUrl,
                    signName: signNameStr,
                    officialSealUrl,
                    officialSealName,
                    signTime: moment().format('YYYY-MM-DD HH:mm:ss')
                })
            });
            data = await res.json();
            if (data && data.status) {
                console.log('签名提交成功:', { certifyId, signName: signNameStr });
                setAlert({
                    open: true, message: '提交成功', close: () => {
                        window.location.href = `/`;
                    }
                });
            } else {
                setIsProcessing(false);
                isProcessingRef.current = false; // 同步解锁
                console.error('签名提交失败:', data);
                setAlert({
                    open: true, message: data?.message || '提交失败', close: () => {
                        setAlert(prev => ({ ...prev, open: false }));
                    }
                });
            }
        } catch (err) {
            setIsProcessing(false);
            isProcessingRef.current = false; // 同步解锁
            console.error('提交签名失败:', err);
            setAlert({
                open: true, message: '提交失败，请检查网络后重试', close: () => {
                    setAlert(prev => ({ ...prev, open: false }));
                }
            });
        }
    }

    const upload = async (file, token) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('id', certifyId);
        const uploadHeaders = {};
        if (token) {
            uploadHeaders['Authorization'] = 'Bearer ' + token;
        }
        let res = await fetch(`/api/upload/signUploadFile`, {
            method: 'POST',
            headers: uploadHeaders,
            body: formData
        });
        res = await res.json();
        console.log('signUploadFile', res);
        if (res && res.success && res.files && res.files.length > 0) {
            return res.files[0].url;
        }
        return "";
    }

    function base64ToFile(base64String, filename) {
        // 检查Base64字符串格式
        if (!base64String.startsWith('data:image/png;base64,')) {
            throw new Error('仅支持PNG格式的Base64字符串');
        }

        // 提取Base64数据部分
        const base64Data = base64String.split(',')[1];

        // 转换为二进制
        const binaryString = atob(base64Data);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // 创建Blob并转为File对象
        const blob = new Blob([bytes], { type: 'image/png' });
        return new File([blob], filename, { type: 'image/png' });
    }

    

    return (
        <div style={{
            width: '100vw',
            minHeight: '100vh',
            fontFamily: 'Arial, sans-serif',
            position: 'relative'
        }}>
            <div style={{
                width: '100vw',
                minHeight: '100vh',
                position: 'absolute',
                left: '0',
                top: '0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                <div style={{
                    position: 'relative',
                    height: 'calc(80vw - 20px)',
                    width: 'calc(80vw - 20px)',
                    marginTop: '5px'
                }}>
                    <div id="tianzige"></div>
                    <div id="tianzige2"></div>
                    <div id="tianzigeName" style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '70vw',
                        lineHeight: 1,
                        color: 'rgba(170, 170, 170, 0.4)',
                        pointerEvents: 'none',
                        fontFamily: '"SimHei", "Heiti SC", "Microsoft YaHei", "PingFang SC", sans-serif',
                        textAlign: 'center',
                        whiteSpace: 'nowrap'
                    }}>
                        {signName && signName.length > 0 && signNameIndex < signName.length ? signName[signNameIndex] : ''}
                    </div>
                    <div style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        left: '0',
                        top: '0',
                        zIndex: 100
                    }}>
                        <SignatureCanvas penColor="black" minWidth={1.5} maxWidth={3.5} canvasProps={{ className: "sigCanvas" }} ref={sigCanvas} />
                    </div>
                </div>
                <div style={{ width: 'calc(100vw - 15px)', height: '35px', display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '10px', zIndex: '1000' }}>
                    <div style={{
                        flex: 1, height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: '#0848f7', color: '#fff', fontSize: '13px', marginRight: '10px', borderRadius: '5px'
                    }} onClick={() => {
                        if (sigCanvas.current) sigCanvas.current.clear();
                    }}>
                        重置
                    </div>
                    <div style={{
                        flex: 1, height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: '#0848f7', color: '#fff', fontSize: '13px', marginLeft: '10px', borderRadius: '5px'
                    }} onClick={getCanvasImage}>
                        确定
                    </div>
                </div>
                <div style={{ fontSize: '15px', width: '100%', paddingLeft: '10px', marginTop: '5px', minHeight: '22px' }}>
                    {signName ? (
                        <>
                            <span style={{ fontSize: '15px', color: 'red' }}>*</span>
                            请在田字格中依次逐字正楷签写：
                            <span style={{ fontSize: '15px', color: '#0847f7', fontWeight: 'bold' }}>{signName}</span>
                        </>
                    ) : isLoading ? (
                        <span style={{ color: '#999' }}>正在加载签名信息...</span>
                    ) : (
                        <span style={{ color: 'red' }}>签名信息加载失败，请刷新页面重试</span>
                    )}
                </div>
                <div style={{ width: 'calc(100vw - 40px)', height: '50px', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <div id="signResultPreview" style={{ flex: '1', height: '100%', border: '1px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

                    </div>
                    <div style={{
                        height: '100%', display: 'flex', justifyContent: 'center',
                        alignItems: 'center', color: '#0746f6', fontSize: '15px', margin: '0 10px 0 5px',
                        cursor: 'pointer'
                    }} onClick={() => {
                        if (isProcessingRef.current) return;
                        setSignNameIndex(0);
                        const currentName = signNameRef.current;
                        const signResultList = new Array(currentName.length).fill(null);
                        setSignResults(signResultList);
                        const resultPreview = document.getElementById('signResultPreview');
                        if (resultPreview) resultPreview.innerHTML = '';
                        if (sigCanvas.current) sigCanvas.current.clear();
                        setOfficialSealFile(null);
                        setOfficialSealPreview(null);
                    }}>
                        清空
                    </div>
                </div>
                {isSignFile ? <div style={{
                    width: '100vw', display: 'flex', flexDirection: 'column', marginTop: '10px', paddingLeft: '10px', paddingRight: '10px'
                }}>
                    <div style={{ fontSize: '13px', marginBottom: '8px', lineHeight: '1.5' }}>
                        <span style={{ color: 'red' }}>*</span>
                        请上传{companyName ? <span style={{ fontWeight: 'bold' }}>【{companyName}】</span> : '【法人单位】'}公章印模并同意授权制作电子印章仅限于办理本次业务：
                    </div>
                    {!officialSealPreview ? (
                        <div
                            onClick={() => {
                                const input = document.getElementById('pick-file-input-sign');
                                input.value = null;
                                if (input) input.click();
                            }}
                            style={{
                                width: 'auto',
                                height: '40px',
                                fontSize: '14px',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                                marginBottom: '0',
                                cursor: 'pointer',
                                backgroundColor: '#f5222d',
                                color: '#fff',
                                borderRadius: '5px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'center',
                                boxSizing: 'border-box'
                            }}
                        >+ 上传公章且授权制作电子印章</div>
                    ) : (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <img
                                src={officialSealPreview}
                                alt="公章预览"
                                onClick={() => setSealModalOpen(true)}
                                style={{ maxWidth: '100%', maxHeight: '80px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                            />
                        </div>
                    )}
                    {sealModalOpen && (
                        <div style={{
                            position: 'fixed',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999
                        }} onClick={() => setSealModalOpen(false)}>
                            <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
                                <img
                                    src={officialSealPreview}
                                    alt="公章放大查看"
                                    style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px', display: 'block' }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div
                                    onClick={(e) => { e.stopPropagation(); setSealModalOpen(false); }}
                                    style={{
                                        position: 'absolute',
                                        top: '-16px',
                                        right: '-16px',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: '#fff',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        fontSize: '20px',
                                        color: '#333',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                    }}
                                >×</div>
                            </div>
                        </div>
                    )}
                </div> : null}
            </div>
            <div style={{
                position: 'absolute',
                left: '0',
                bottom: '0',
                width: '100vw',
                padding: '15px 10px',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{ width: '100%', fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                    <input
                        type="checkbox"
                        name="isNotSignFile"
                        checked={checked}
                        onChange={(e) => { setChecked(e.target.checked) }}
                        style={{
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#2196f3',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            marginRight: '4px',
                            verticalAlign: '-2px',
                            ...(isClick ? isClickStyle : {})
                        }}
                    />
                    <span style={{ fontWeight: 'bold' }}>声明：</span>
                    本人已阅读并同意<span style={{ color: '#1f47ee' }}>文件签章处</span>的全部内容，为提高效率，本人在签署页做出一次签名即代表本人承认并签订前述全部文件，接受全部的权利和义务。
                </div>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '10px', marginTop: '10px' }}>
                    {officialSealPreview && (
                        <div style={{
                            flex: 1, height: '40px', backgroundColor: '#fff', color: '#0848f7', fontSize: '15px',
                            display: 'flex', justifyContent: 'center', alignItems: 'center',
                            borderRadius: '5px', border: '1px solid #0848f7', cursor: 'pointer'
                        }} onClick={() => {
                            const input = document.getElementById('pick-file-input-sign');
                            input.value = null;
                            if (input) input.click();
                        }}>重新上传</div>
                    )}
                    <div style={{
                        flex: 1, height: '40px', backgroundColor: '#0848f7', color: '#fff', fontSize: '15px',
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        borderRadius: '5px', cursor: 'pointer'
                    }} onClick={submit}>提交</div>
                </div>
            </div>
            {isLoading && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #0848f7',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 10px'
                        }}></div>
                        <div style={{ color: '#666', fontSize: '14px' }}>加载中...</div>
                    </div>
                </div>
            )}
            <SimpleAlert open={alert.open} message={alert.message} onClose={() => { alert.close() }} />
            <input id="pick-file-input-sign" style={{ display: 'none' }} type="file" accept="image/*" onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                setOfficialSealFile(file);
                setOfficialSealPreview(URL.createObjectURL(file));
            }} />
        </div >
    );
}                   